import { useRef, useState } from 'react'

import { GAMES, TEDDIES, teddyPhotoPath, type CellState, type MosaicSize } from '@hazel/shared'

import { Button } from '../components/Button.js'
import { Card } from '../components/Card.js'
import { KeyCap } from '../components/KeyCap.js'
import { KeyLegend } from '../components/KeyLegend.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { fallBackToDrawing } from '../components/teddyPicture.js'
import { useBarTitle } from '../layouts/bar-title.js'
import { Announce } from './Announce.js'
import { LeaveGame } from './LeaveGame.js'
import { Milestone } from './Milestone.js'
import { MosaicCell } from './MosaicCell.js'
import { PlayAgain } from './PlayAgain.js'
import {
  columnsFor,
  cycle,
  gutterFor,
  MOSAIC_LAYOUT,
  ROOM_FOR_THE_BIGGEST,
  sizesOffered,
  stepFrom,
} from './mosaicLayout.js'
import { useCueOnChange, useRoundEndCues, useRoundMusic } from './gameSound.js'
import { TRACK_FOR_GAME } from '../sound/cues.js'
import { useSound } from '../sound/sound-context.js'
import { digitFrom, useGameKeys } from './useGameKeys.js'
import { useIsWide } from './useIsWide.js'
import { useLeaveOnEscape } from './useLeaveOnEscape.js'
import { useMosaic, type MosaicConfigFromRound } from './useMosaic.js'
import { useRound, type DealtRound } from './useRound.js'
import { useRoundFocus } from './useRoundFocus.js'

/**
 * What each size is called, and what it turns into.
 *
 * The second line is the point, and it is the honest version of a promise the
 * ticket made everywhere. Twenty-five squares cannot be anybody in particular,
 * so the small pictures are things rather than teddies — and she should know
 * that before she picks rather than feel short-changed after.
 *
 * Ten across said "a teddy's face" until the set grew: Nemo, Chaka and
 * Maritza are drawn whole at that size, because a fish, a parrot and a cactus
 * are more themselves in outline than in close-up. So it says "a teddy",
 * which is true of all of them.
 */
const SIZE_LABELS: Readonly<Record<MosaicSize, { name: string; becomes: string }>> = {
  5: { name: 'Small', becomes: 'A little picture' },
  10: { name: 'Middling', becomes: 'A teddy' },
  15: { name: 'The big one', becomes: 'A bigger teddy' },
}

/**
 * How big a picture, asked before there is one.
 *
 * She picks, exactly as she picks a grid in Stripey's Sudoku.
 *
 * **The biggest is not offered on a phone**, and that is a fact about the game
 * rather than about taste. A nonogram is not only its grid: the clues live in
 * a gutter along the top and down the side, so fifteen across is twenty-three
 * cells wide — seventeen pixels a cell on a 390px screen, with numbers inside
 * them. Offering it there would be offering something she cannot play. Which
 * sizes a screen gets is decided in `mosaicLayout.ts`, beside the table that
 * draws them.
 */
function ChooseSize({ onChoose }: { onChoose: (size: MosaicSize) => void }) {
  const leaveOnEscape = useLeaveOnEscape()
  const hasRoom = useIsWide(ROOM_FOR_THE_BIGGEST)
  const sizes = sizesOffered(hasRoom)

  useGameKeys((event) => {
    if (event.key === 'Escape') return void leaveOnEscape(false)

    const digit = digitFrom(event)
    if (!digit) return

    const chosen = sizes[Number(digit) - 1]
    if (chosen !== undefined) onChoose(chosen)
  })

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-5 [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:gap-3">
      <Card className="flex w-full flex-col items-center gap-4">
        <h2 className="text-(length:--text-heading)">How big a picture?</h2>

        <div className="flex w-full flex-col gap-2 [[data-scale=tv]_&]:gap-1">
          {sizes.map((option, index) => (
            <Button
              key={option}
              variant="secondary"
              block
              onClick={() => onChoose(option)}
              aria-label={`${SIZE_LABELS[option].name}, ${option} by ${option}, ${SIZE_LABELS[option].becomes}. Press ${index + 1}`}
            >
              <span className="flex w-full items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <span aria-hidden="true" className="[@media(pointer:coarse)]:hidden">
                    <KeyCap>{index + 1}</KeyCap>
                  </span>
                  {SIZE_LABELS[option].name}
                </span>
                <span className="text-ink-muted text-(length:--text-small)">
                  {`${option} × ${option} · ${SIZE_LABELS[option].becomes}`}
                </span>
              </span>
            </Button>
          ))}
        </div>
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={[
          { keys: ['1', '–', String(sizes.length)], does: 'Pick a size' },
          LEAVE_SHORTCUT,
        ]}
      />
    </div>
  )
}

export function Mosaic() {
  // The bar carries the name while she is here, in place of "Hazel's
  // Games". One call rather than one per screen: the chooser and the
  // board are both this component, so the name does not blink out between
  // picking a size and being dealt one.
  useBarTitle(GAMES.mosaic.title)

  /*
    Stripey's loop, on purpose and for now.

    Both are quiet sit-down puzzles and the track suits either, so this shares
    one rather than shipping a game with no music at all. A `mosaic` track is
    one line here the day there is a file for it.
  */
  useRoundMusic(TRACK_FOR_GAME.mosaic)

  const [size, setSize] = useState<MosaicSize | null>(null)

  if (size === null) return <ChooseSize onChoose={setSize} />

  return <MosaicRound size={size} />
}

function MosaicRound({ size }: { size: MosaicSize }) {
  const { round, again, failed } = useRound<MosaicConfigFromRound>({ game: 'mosaic', size })

  if (failed) {
    return (
      <p role="alert" className="text-coral py-16 text-center">
        We couldn’t start a game just now. Have another go in a moment.
      </p>
    )
  }

  if (!round) {
    return (
      <p role="status" className="py-16 text-center">
        Drawing the squares…
      </p>
    )
  }

  return <MosaicGrid key={round.seed} round={round} size={size} onAgain={again} />
}

function MosaicGrid({
  round,
  size,
  onAgain,
}: {
  round: DealtRound<MosaicConfigFromRound>
  size: MosaicSize
  onAgain: () => void
}) {
  const leaveOnEscape = useLeaveOnEscape()
  const game = useMosaic(round, onAgain)
  const sound = useSound()
  const { view } = game
  const board = useRoundFocus<HTMLDivElement>(round.seed, view.status !== 'playing')

  /** Where the keyboard is pointing. */
  const [cursor, setCursor] = useState(0)

  useCueOnChange(cursor, 'move')
  useRoundEndCues(view.status !== 'playing', game.milestone)

  const columns = columnsFor(size)
  const layout = MOSAIC_LAYOUT[size]
  const gutter = gutterFor(view.clues)

  /** The square buttons, so the cursor can carry real focus with it. */
  const cells = useRef(new Map<number, HTMLButtonElement>())

  function moveTo(next: number | null): void {
    if (next === null) return

    setCursor(next)

    // Focus goes where the ring goes, every time.
    const target = cells.current.get(next)
    target?.focus()
    if (document.activeElement !== target) board.current?.focus()
  }

  /**
   * Puts a square in a state — by default the one under the cursor.
   *
   * The square is an argument rather than always `cursor` because a tap has to
   * say which one it meant. `setCursor` does not take effect until the next
   * render, so a tap that moved the cursor and then acted "on the cursor"
   * acted on the square she had been on before — it wrote to the previous
   * square and left the one she touched alone. Every keyboard test passed over
   * that, because the keyboard only ever acts where the cursor already is.
   */
  function put(next: CellState, cell: number = cursor): void {
    const was = view.squares[cell]
    // Pressing the same key twice changes nothing, and should not sound as
    // though it did.
    if (was === next) return

    sound.play(next === 'unknown' ? 'tile.back' : 'tile.take')
    game.set(cell, next)
  }

  useGameKeys((event) => {
    // Before the status gate: a finished picture is exactly when somebody
    // wants out, and there is nothing to lose at any point.
    if (event.key === 'Escape') return void leaveOnEscape(false)

    if (view.status !== 'playing') {
      if (event.key === 'Enter') game.again()
      return
    }

    switch (event.key) {
      case 'ArrowLeft':
        return moveTo(stepFrom(size, cursor, 'left'))
      case 'ArrowRight':
        return moveTo(stepFrom(size, cursor, 'right'))
      case 'ArrowUp':
        return moveTo(stepFrom(size, cursor, 'up'))
      case 'ArrowDown':
        return moveTo(stepFrom(size, cursor, 'down'))
      case 'Backspace':
      case 'Delete':
        event.preventDefault()
        return put('unknown')
      default:
    }

    /*
     * Space and Enter both cycle, and F and X go straight there.
     *
     * Cycling is the ticket's ask and the two big keys are what a hand finds
     * without looking. The named keys are here because marking blanks is most
     * of the work in a nonogram — you find the empties and the filled squares
     * fall out — and reaching a mark by pressing Space twice gets old across
     * two hundred squares.
     *
     * **Space and Enter reach a square by two different routes, and it is
     * worth knowing which.** `moveTo` puts real focus on the square button so
     * the ring and the browser's own focus are the same thing — so once she
     * has pressed an arrow, a focused `<button>` is what Space is aimed at.
     * `useGameKeys` refuses Space and Enter outright in that case, exactly as
     * CLAUDE.md requires, and the browser's own button activation fires a
     * click, which lands on `onPick` and cycles the square.
     *
     * The branch below is what handles the other case: the board itself holds
     * focus when a round opens, before any arrow has been pressed, and there
     * is no button for the browser to activate.
     *
     * An earlier version of this comment claimed focus sits on the board and
     * these keys never reach a square button. That was false the moment she
     * pressed an arrow — review caught it — and it mattered because it
     * described a safety mechanism that was not the one doing the work. Both
     * routes are tested, at cell nought and after moving.
     */
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      return put(cycle(view.squares[cursor] ?? 'unknown'))
    }

    // Upper and lower case, because it is one key to her and Caps Lock is not
    // a state anybody checks.
    if (event.key === 'f' || event.key === 'F') {
      event.preventDefault()
      return put(view.squares[cursor] === 'filled' ? 'unknown' : 'filled')
    }

    if (event.key === 'x' || event.key === 'X') {
      event.preventDefault()
      return put(view.squares[cursor] === 'marked' ? 'unknown' : 'marked')
    }

    if (event.key === 'r' || event.key === 'R') {
      event.preventDefault()
      sound.play('grid.clear')
      game.clear()
    }
  })

  const reveal = view.reveal
  const teddy = reveal?.teddy ? TEDDIES[reveal.teddy] : undefined

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-4 [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1">
      <Card
        ref={board}
        tabIndex={-1}
        className="outline-none flex w-full flex-col items-center gap-4 [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1 [[data-scale=tv]_&]:flex-row [[data-scale=tv]_&]:items-stretch [[data-scale=tv]_&]:gap-6"
      >
        <div className="contents [[data-scale=tv]_&]:order-2 [[data-scale=tv]_&]:flex [[data-scale=tv]_&]:w-72 [[data-scale=tv]_&]:flex-none [[data-scale=tv]_&]:flex-col [[data-scale=tv]_&]:justify-center [[data-scale=tv]_&]:gap-6">
          {/*
            Squares filled, never squares *right*. Announcing correctness would
            be announcing the answer — the same rule Stripey's live region and
            Stripey's colours are both held to.
          */}
          <Announce>
            {[
              view.status === 'playing'
                ? `${view.filledIn} of ${view.toFill} squares filled in`
                : `You got it. It’s ${reveal?.title ?? 'a picture'}.`,
              game.milestone ? `That’s ${game.milestone} games played.` : '',
            ]
              .filter(Boolean)
              .join(' ')}
          </Announce>

          {view.status === 'playing' ? (
            <>
              <p className="text-ink-muted text-(length:--text-small)">
                {`${view.filledIn} of ${view.toFill} squares filled in`}
              </p>

              {/*
                The controls, and on a phone the only way to play at all.
                Hidden on the television, which has the keys and a legend.
              */}
              <div className="flex flex-wrap justify-center gap-2 [[data-scale=tv]_&]:hidden">
                <Button
                  variant="secondary"
                  onClick={() => put(view.squares[cursor] === 'filled' ? 'unknown' : 'filled')}
                >
                  Fill it in
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => put(view.squares[cursor] === 'marked' ? 'unknown' : 'marked')}
                >
                  Mark it blank
                </Button>
                <Button variant="secondary" onClick={game.clear}>
                  Start again
                </Button>
              </div>
            </>
          ) : (
            /*
              The payoff, and the whole reason this game is not a second sudoku.

              Every other game tells her how a round went in the round's own
              terms. This one hands her something she made by reasoning, so the
              finish says what it turned out to be and shows the teddy beside
              it — and the tally and the buttons get out of the way, the same
              lesson Stripey's finish learned when its column ran off the
              bottom of the television.
            */
            <div className="flex flex-col items-center gap-3">
              <p className="font-display text-(length:--text-title)">
                {reveal?.teddy ? `It’s ${reveal.title}!` : `It’s ${reveal?.title ?? 'finished'}!`}
              </p>

              {teddy ? (
                <img
                  src={teddyPhotoPath(teddy.key)}
                  alt={teddy.name}
                  className="border-border size-28 rounded-(--radius-control) border-2 object-cover [[data-scale=tv]_&]:size-40"
                  onError={(event) => fallBackToDrawing(event.currentTarget, teddy.key)}
                />
              ) : null}

              <p className="text-ink-muted [[data-scale=tv]_&]:text-(length:--text-heading)">
                {view.changes === 0
                  ? 'And never changed your mind once.'
                  : `${view.changes} ${view.changes === 1 ? 'change' : 'changes'} of mind along the way.`}
              </p>

              <PlayAgain label="Another picture" does="for another picture" onAgain={game.again} />
              {game.milestone ? <Milestone games={game.milestone} /> : null}
            </div>
          )}
        </div>

        {/*
          The picture, with its clues around it.

          One CSS grid holding both, rather than three boxes lined up by hand:
          the gutter cells and the squares have to share column and row tracks
          exactly, or a clue drifts a pixel out from the line it is about and
          the whole thing stops being readable.

          The track count is set inline rather than as a class because it is
          not knowable at build time — the gutter is as wide as the longest
          clue *in this picture*, so a Tailwind class would have to exist for
          every count a picture might want. `--across` is the honest version of
          that, and the squares stay square by aspect ratio rather than by
          arithmetic.
        */}
        <div className="w-full min-w-0 overflow-x-auto [[data-scale=tv]_&]:order-1 [[data-scale=tv]_&]:flex [[data-scale=tv]_&]:min-h-0 [[data-scale=tv]_&]:flex-1 [[data-scale=tv]_&]:items-center [[data-scale=tv]_&]:justify-center">
          <div
            /*
              `group`, not `grid` — the same rule as Stripey's board, and for
              the same reason: an ARIA grid promises rows and gridcells, and
              this is one flat CSS grid of buttons and clue spans. Mosaic could
              not honour it even if it wanted to, because the clue gutter shares
              the squares' tracks and that sharing is what keeps a clue aligned
              with the line it is about.

              Every square says "Row 2, column 3, marked as blank" on its own,
              so the position is spoken either way.
            */
            role="group"
            aria-label="The picture"
            className="mx-auto grid w-full max-w-[min(100%,42rem)] [[data-scale=tv]_&]:h-full [[data-scale=tv]_&]:w-auto [[data-scale=tv]_&]:max-w-none"
            style={{
              gridTemplateColumns: `repeat(${gutter.across + columns}, minmax(0, 1fr))`,
              aspectRatio: `${gutter.across + columns} / ${gutter.down + columns}`,
            }}
          >
            {/* The clue numbers along the top, sitting above their column. */}
            {Array.from({ length: gutter.down }, (_, line) =>
              Array.from({ length: gutter.across + columns }, (_, at) => {
                if (at < gutter.across) {
                  return <span key={`pad-${line}-${at}`} aria-hidden="true" />
                }

                const clue = view.clues.columns[at - gutter.across] ?? []
                // Bottom-aligned, so the last number of every clue sits right
                // above the grid and she reads up from the square she is on.
                const from = gutter.down - clue.length
                const run = line >= from ? clue[line - from] : undefined

                return (
                  <span
                    key={`top-${line}-${at}`}
                    aria-hidden="true"
                    className={[
                      'text-ink-muted flex items-center justify-center leading-none',
                      layout.clueClassName,
                    ].join(' ')}
                  >
                    {run ?? ''}
                  </span>
                )
              }),
            )}

            {Array.from({ length: layout.rows }, (_, row) => {
              const clue = view.clues.rows[row] ?? []
              const from = gutter.across - clue.length

              return [
                // Right-aligned, so the last number of every clue sits against
                // the grid for the same reason the top ones sit low.
                ...Array.from({ length: gutter.across }, (_, at) => {
                  const run = at >= from ? clue[at - from] : undefined

                  return (
                    <span
                      key={`side-${row}-${at}`}
                      aria-hidden="true"
                      className={[
                        'text-ink-muted flex items-center justify-center leading-none',
                        layout.clueClassName,
                      ].join(' ')}
                    >
                      {run ?? ''}
                    </span>
                  )
                }),
                ...Array.from({ length: columns }, (_, column) => {
                  const cell = row * columns + column

                  return (
                    <MosaicCell
                      key={cell}
                      cell={cell}
                      columns={columns}
                      state={view.squares[cell] ?? 'unknown'}
                      cursor={cursor === cell}
                      squareClassName={layout.squareClassName}
                      ref={(element) => {
                        if (element) cells.current.set(cell, element)
                        else cells.current.delete(cell)
                      }}
                      onPick={(element) => {
                        setCursor(cell)
                        element?.focus()
                        if (view.status === 'playing') {
                          put(cycle(view.squares[cell] ?? 'unknown'), cell)
                        }
                      }}
                    />
                  )
                }),
              ]
            })}
          </div>
        </div>
      </Card>

      <LeaveGame />

      <KeyLegend
        shortcuts={[
          { keys: ['←', '→', '↑', '↓'], does: 'Move' },
          { keys: ['Space'], does: 'Fill, mark, empty' },
          { keys: ['F'], does: 'Fill it in' },
          { keys: ['X'], does: 'Mark it blank' },
          { keys: ['R'], does: 'Start again' },
          LEAVE_SHORTCUT,
        ]}
      />
    </div>
  )
}
